package com.essntl.uikit.components.image

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.DefaultAlpha
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import com.essntl.core.network.ImageClient
import com.essntl.uikit.components.svg.loadSvgPainterFromBytes
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@Composable
fun AsyncUrlImage(
    url: String,
    placeholder: @Composable (BoxScope.() -> Unit) = {},
    error: @Composable (BoxScope.() -> Unit)? = null,
    contentDescription: String? = null,
    modifier: Modifier = Modifier,
    alignment: Alignment = Alignment.Center,
    contentScale: ContentScale = ContentScale.Fit,
    alpha: Float = DefaultAlpha,
    colorFilter: ColorFilter? = null,
) {
    key(url) {
        if (url.endsWith(".svg")) {
            val imageClient = koinInject<ImageClient>()

            AsyncImage(
                load = {
                    imageClient.get(url)
                },
                painterFor = {
                    loadSvgPainterFromBytes(it)
                },
                placeholder = placeholder,
                error = error,
                contentDescription = contentDescription,
                modifier = modifier,
                alignment = alignment,
                contentScale = contentScale,
                alpha = alpha,
                colorFilter = colorFilter,
            )
        } else {
            AsyncImage(
                data = url,
                placeholder = placeholder,
                error = error,
                contentDescription = contentDescription,
                modifier = modifier,
                alignment = alignment,
                contentScale = contentScale,
                alpha = alpha,
                colorFilter = colorFilter,
            )
        }
    }
}

@Composable
fun AsyncImage(
    data: Any,
    placeholder: @Composable BoxScope.() -> Unit = {},
    error: (@Composable BoxScope.() -> Unit)? = null,
    contentDescription: String? = null,
    modifier: Modifier = Modifier,
    alignment: Alignment = Alignment.Center,
    contentScale: ContentScale = ContentScale.Fit,
    alpha: Float = DefaultAlpha,
    colorFilter: ColorFilter? = null,
) {
    var isLoading by remember {
        mutableStateOf(true)
    }

    var isFailure by remember {
        mutableStateOf(false)
    }

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier,
    ) {
        coil3.compose.AsyncImage(
            model = ImageRequest.Builder(LocalPlatformContext.current)
                .data(data)
                .build(),
            contentDescription = contentDescription,
            onSuccess = {
                isFailure = false
                isLoading = false
            },
            onError = {
                isFailure = true
                isLoading = false
            },
            alignment = alignment,
            contentScale = contentScale,
            alpha = alpha,
            colorFilter = colorFilter,
            modifier = Modifier.fillMaxSize(),
        )

        if (isFailure)
            if (error != null)
                error()
            else
                placeholder()
        else if (isLoading)
            placeholder()
    }
}

@Composable
internal fun <T> AsyncImage(
    load: suspend () -> T,
    painterFor: @Composable (T) -> Painter,
    placeholder: @Composable BoxScope.() -> Unit = {},
    error: (@Composable BoxScope.() -> Unit)? = null,
    contentDescription: String? = null,
    modifier: Modifier = Modifier,
    alignment: Alignment = Alignment.Center,
    contentScale: ContentScale = ContentScale.Fit,
    alpha: Float = DefaultAlpha,
    colorFilter: ColorFilter? = null,
) {
    var isLoading by remember {
        mutableStateOf(false)
    }

    var isFailure by remember {
        mutableStateOf(false)
    }

    var image: T? by remember {
        mutableStateOf(null)
    }

    LaunchedEffect(Unit) {
        launch {
            image =
                try {
                    isLoading = true
                    load()
                } catch (e: CancellationException) {
                    // ignore cancellation exception, it happens when the composable is disposed
                    null
                } catch (e: Exception) {
                    e.printStackTrace()
                    // instead of printing to console, you can also write this to log,
                    // or show some error placeholder
                    isFailure = true
                    null
                } finally {
                    isLoading = false
                }
        }
    }

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier,
    ) {
        if (image != null) {
            image?.let {
                Image(
                    painter = painterFor(it),
                    contentDescription = contentDescription,
                    modifier = Modifier.fillMaxSize(),
                    alignment = alignment,
                    contentScale = contentScale,
                    alpha = alpha,
                    colorFilter = colorFilter,
                )
            }
        } else if (isFailure && error != null) {
            error()
        } else {
            placeholder()
        }
    }
}

suspend fun loadUrlImageBitmap(imageClient: ImageClient, url: String): ImageBitmap =
    imageClient.get(url).toImageBitmap()